/**
 * The <wa-page> application shell.
 *
 * Built once and reused; the router swaps only the main region. wa-page is used
 * because it provides, for free, two things Phase 1's acceptance criteria need:
 * a built-in skip-to-content link and a responsive navigation drawer that
 * collapses on mobile. Re-implementing either by hand would be more code and
 * more ways to get accessibility wrong.
 */

import { el, icon } from "../ui/dom.js";
import { supportHref } from "../config/support.js";

/**
 * @typedef {object} Shell
 * @property {HTMLElement} element The <wa-page> root.
 * @property {(content: Node, heading: string) => void} setMain Replace the main
 *   region's content and move focus to its heading.
 * @property {HTMLElement} navElement The navigation container.
 */

/** Primary navigation links. Kept small; retrieval is the public priority. */
const NAV_LINKS = [
  { href: "/", label: "Home", iconName: "house" },
  { href: "/retrieve", label: "Retrieve contacts", iconName: "key" },
  { href: "/dashboard", label: "Your profiles", iconName: "users" },
  { href: "/pricing", label: "Pricing", iconName: "credit-card" },
  { href: "/account", label: "Account", iconName: "gear" },
];

/**
 * Build the shell. Navigation happens through `data-link` anchors that the router
 * intercepts, so the shell needs no navigate callback of its own.
 *
 * @returns {Shell}
 */
export function renderShell() {
  // The brand mark is an <img>, not an inline <svg> or a wa-icon: it is a logo
  // rather than an interface icon, so it must not inherit `currentColor` or be
  // restyled by icon rules. It carries no alt text because the adjacent word
  // "KinCode" already names the link — alt="KinCode" here would make a screen
  // reader announce the name twice.
  const brand = el("a", { href: "/", class: "kin-brand", dataset: { link: "" } }, [
    el("img", {
      src: "/icon.svg",
      alt: "",
      width: "28",
      height: "28",
      class: "kin-brand-mark",
      decoding: "async",
    }),
    el("span", {}, ["KinCode"]),
  ]);

  const nav = el("nav", { class: "kin-nav", "aria-label": "Primary" }, [
    el(
      "ul",
      {},
      NAV_LINKS.map((link) =>
        el("li", {}, [
          el("a", { href: link.href, dataset: { link: "", navHref: link.href } }, [
            icon(link.iconName),
            el("span", {}, [link.label]),
          ]),
        ]),
      ),
    ),
  ]);

  const main = el("main", { id: "main-content", class: "kin-content kin-stack", tabindex: "-1" });

  const footer = el("footer", { class: "kin-footer" }, [
    el("nav", { "aria-label": "Footer" }, [
      el("a", { href: "/privacy", dataset: { link: "" } }, ["Privacy"]),
      el("a", { href: "/terms", dataset: { link: "" } }, ["Terms"]),
      // A plain mailto, not a router link: support is reachable from every page,
      // including when the app itself has failed (the error boundary shows the
      // same address). Nothing is logged either side of it.
      el("a", { href: supportHref("KinCode — support") }, ["Contact support"]),
    ]),
    el("p", { class: "kin-footer-note" }, [
      "KinCode helps you recover contact details. It is not an emergency service.",
    ]),
  ]);

  const page = el("wa-page", { view: "desktop", "mobile-breakpoint": "768" }, [
    el("header", { slot: "header", class: "kin-header" }, [
      brand,
      el("wa-button", { slot: "navigation-toggle", appearance: "plain", "data-toggle-nav": "" }, [
        icon("bars", { label: "Toggle navigation" }),
      ]),
    ]),
    el("div", { slot: "navigation" }, [nav]),
    main,
    el("div", { slot: "footer" }, [footer]),
  ]);

  /** @type {Shell} */
  const shell = {
    element: page,
    navElement: nav,
    setMain(content, heading) {
      main.replaceChildren(content);
      // Close the mobile navigation drawer. The drawer covers the page, so a link
      // tapped inside it navigates behind an unchanged screen and the change is
      // invisible until the drawer is dismissed by hand. Done here rather than on
      // the link click so that programmatic navigation and the browser's back and
      // forward buttons are covered too.
      //
      // Known limitation: wa-page's drawer is a native <dialog>, so closing it
      // returns focus to the navigation toggle a few milliseconds later, undoing
      // the focus move below. Focus therefore ends at the top of the new page
      // rather than on its main region — correct page, wrong landmark. Racing it
      // with a timer was tried and is not reliable; see the notes in
      // tests/e2e/navigation.spec.js.
      /** @type {{ hideNavigation?: () => void }} */ (
        /** @type {unknown} */ (page)
      ).hideNavigation?.();
      // Move focus to the main region on navigation so keyboard and screen-reader
      // users land on the new content rather than staying on a now-gone control.
      main.setAttribute("aria-label", heading);
      main.focus();
    },
  };

  return shell;
}

/**
 * Mark the nav link matching the current path as current.
 *
 * @param {Shell} shell
 * @param {string} path
 * @returns {void}
 */
export function setActiveNav(shell, path) {
  const links = shell.navElement.querySelectorAll("a[data-nav-href]");
  for (const link of links) {
    if (link.getAttribute("data-nav-href") === path) {
      link.setAttribute("aria-current", "page");
    } else {
      link.removeAttribute("aria-current");
    }
  }
}
